import api from './api';
import { getApiUrl } from './config';
import { handleApiCall } from '../utils/apiResponseHandler';
import { endpoint } from './endpoints';

export interface DuplicateAccountFlag {
  id: string;
  flaggedUser: {
    id: string;
    email: string;
    name: string;
    npn: string;
    phone: string;
    createdAt: string;
  };
  existingUser: {
    id: string;
    email: string;
    name: string;
    npn: string;
    phone: string;
    createdAt: string;
  };
  duplicateNpn: string;
  flagReason: string;
  status: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  createdAt: string;
  reviewedAt?: string;
  resolvedAt?: string;
  reviewedBy?: string;
  resolvedBy?: string;
  adminNotes?: string;
  resolutionAction?: 'merge' | 'separate' | 'suspend_duplicate' | 'no_action';
}

export interface DuplicateAccountStats {
  total: number;
  pending: number;
  reviewed: number;
  resolved: number;
  dismissed: number;
  thisWeek: number;
  thisMonth: number;
}

export interface DuplicateAccountFilters {
  status?: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface DuplicateAccountResponse {
  flags: DuplicateAccountFlag[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

class DuplicateAccountService {
  /**
   * Get all duplicate account flags with filtering and pagination
   */
  async getDuplicateAccountFlags(filters: DuplicateAccountFilters = {}): Promise<DuplicateAccountResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.status) params.append('status', filters.status);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.search) params.append('search', filters.search);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const response = await handleApiCall<DuplicateAccountResponse>(async () => await api.get({
        url: getApiUrl(endpoint.admin.duplicateAccounts.flags + (params.toString() ? `?${params.toString()}` : '')),
        auth: true // Add authentication
      }));
      // handleApiCall already extracts the 'data' property, so response is the extracted data
      return response;
    } catch (error) {

      throw new Error('Failed to fetch duplicate account flags');
    }
  }

  /**
   * Get duplicate account statistics
   */
  async getDuplicateAccountStats(): Promise<DuplicateAccountStats> {
    try {
      const response = await handleApiCall<DuplicateAccountStats>(async () => await api.get({
        url: getApiUrl(endpoint.admin.duplicateAccounts.stats),
        auth: true // Add authentication
      }));
      // handleApiCall already extracts the 'data' property
      return response;
    } catch (error) {

      throw new Error('Failed to fetch duplicate account statistics');
    }
  }

  /**
   * Get a specific duplicate account flag by ID
   */
  async getDuplicateAccountFlag(flagId: string): Promise<DuplicateAccountFlag> {
    try {
      const response = await handleApiCall<DuplicateAccountFlag>(async () => await api.get({
        url: getApiUrl(endpoint.admin.duplicateAccounts.getFlags(flagId)),
        auth: true // Add authentication
      }));
      return response;
    } catch (error) {

      throw new Error('Failed to fetch duplicate account flag');
    }
  }

  /**
   * Review a duplicate account flag
   */
  async reviewFlag(flagId: string, adminNotes: string): Promise<DuplicateAccountFlag> {
    try {
      const response = await handleApiCall<DuplicateAccountFlag>(async () => await api.put({
        url: getApiUrl(endpoint.admin.duplicateAccounts.reviewFlag(flagId)),
        body: { adminNotes },
        auth: true // Add authentication
      }));
      return response;
    } catch (error) {

      throw new Error('Failed to review flag');
    }
  }

  /**
   * Resolve a duplicate account flag
   */
  async resolveFlag(
    flagId: string,
    resolutionAction: 'merge' | 'separate' | 'suspend_duplicate' | 'no_action',
    adminNotes: string
  ): Promise<DuplicateAccountFlag> {
    try {
      const response = await handleApiCall<DuplicateAccountFlag>(async () => await api.put({
        url: getApiUrl(endpoint.admin.duplicateAccounts.resolveFlag(flagId)),
        body: { resolutionAction, adminNotes },
        auth: true // Add authentication
      }));
      return response;
    } catch (error) {

      throw new Error('Failed to resolve flag');
    }
  }

  /**
   * Dismiss a duplicate account flag
   */
  async dismissFlag(flagId: string, adminNotes: string): Promise<DuplicateAccountFlag> {
    try {
      const response = await handleApiCall<DuplicateAccountFlag>(async () => await api.put({
        url: getApiUrl(endpoint.admin.duplicateAccounts.dismissFlag(flagId)),
        body: { adminNotes },
        auth: true // Add authentication
      }));
      return response;
    } catch (error) {

      throw new Error('Failed to dismiss flag');
    }
  }

  /**
   * Get duplicate accounts by NPN
   */
  async getDuplicatesByNPN(npn: string): Promise<DuplicateAccountFlag[]> {
    try {
      const response = await handleApiCall<DuplicateAccountFlag[]>(async () => await api.get({
        url: getApiUrl(endpoint.admin.duplicateAccounts.getByNPN(npn)),
        auth: true // Add authentication
      }));
      return response;
    } catch (error) {

      throw new Error('Failed to fetch duplicates by NPN');
    }
  }

  /**
   * Get duplicate accounts by email
   */
  async getDuplicatesByEmail(email: string): Promise<DuplicateAccountFlag[]> {
    try {
      const response = await handleApiCall<DuplicateAccountFlag[]>(async () => await api.get({
        url: getApiUrl(endpoint.admin.duplicateAccounts.getByEmail(email)),
        auth: true // Add authentication
      }));
      return response;
    } catch (error) {

      throw new Error('Failed to fetch duplicates by email');
    }
  }

  /**
   * Get duplicate accounts by phone
   */
  async getDuplicatesByPhone(phone: string): Promise<DuplicateAccountFlag[]> {
    try {
      const response = await handleApiCall<DuplicateAccountFlag[]>(async () => await api.get({
        url: getApiUrl(endpoint.admin.duplicateAccounts.getByPhone(phone)),
        auth: true // Add authentication
      }));
      return response;
    } catch (error) {

      throw new Error('Failed to fetch duplicates by phone');
    }
  }
}

export const duplicateAccountService = new DuplicateAccountService();
